import { ColDef, GridApi, GridOptions, Module } from '@ag-grid-community/core'
import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core'
import {
  disableChangeDetectionOnScroll,
  enableHeliosCopyBehavior,
  enableHeliosGridDensityLevel,
  heliosClientSideRowConfig,
} from '@helios/ag-grid-config'
import { DensityLevel, UserPreferencesService } from '@helios/user-preferences'
import { prettyFormatNumber } from '@helios/utils'
import { Subject } from 'rxjs'
import { map, take, takeUntil, tap } from 'rxjs/operators'
import {
  PositionComparison,
  PositionComparisonExtToExt,
  PositionComparisons,
  PositionComparisonsExtToExt,
  PositionComparisonTitles,
} from '../model/position-comparison.model'
import { Position, PositionType } from '../model/positions.model'
import { PositionsService } from '../services/positions.service'
import { FormControl } from '@angular/forms'

interface PositionComparisonDataBase {
  title: string
}

interface PositionComparisonData
  extends PositionComparisonDataBase,
    PositionComparison<unknown> {}

interface PositionComparisonDataExtToExt
  extends PositionComparisonDataBase,
    PositionComparisonExtToExt<unknown> {}

// The merged type
type PositionComparisonDataMerged =
  | PositionComparisonData
  | PositionComparisonDataExtToExt

/**
 * Format the column values
 */
export function columnValueFormatter(value: unknown) {
  if (value === 0) {
    // Return itself just in case it's a zero
    return '0'
  }

  if (!value) {
    return ''
  }

  if (!isNaN(Number(value))) {
    return prettyFormatNumber(Number(value), 4)
  }

  return value
}

@Component({
  selector: 'hs-external-comparison',
  templateUrl: './external-comparison-panel.component.html',
  styleUrls: ['./external-comparison-panel.component.scss'],
})
export class ExternalComparisonPanelComponent implements OnDestroy {
  positionComparisonData = new Subject<PositionComparisonDataMerged[]>()

  includeValuationData = false
  clearedOnly = false
  propertiesTitle: string[] = []
  isExternalToExternalComparison = false
  displayDynamicRows = new FormControl(false)

  private readonly destroySubject$ = new Subject<void>()

  _position?: Position
  @Input() set position(value: Position | undefined) {
    if (this._position != value) {
      this._position = value
      this.includeValuationData = false
      this.clearedOnly = false
      this.isExternalToExternalComparison =
        this._position?.isExternalToExternalModeSelected ?? false
      this.reloadData()
    }
  }

  @Input() set rowsUpdated(value: string[] | null) {
    if (!this._position || !value?.includes(this._position.positionId)) {
      return
    }
    this.reloadData()
  }

  hideSearch = true
  hideClearAll = true

  readonly gridModules: Module[] = []

  columns: ColDef[] = this.createColumnDefs()

  /** @internal */
  gridApi?: GridApi
  gridOptions: GridOptions = {
    columnDefs: this.columns,
    ...heliosClientSideRowConfig,
    defaultColDef: {
      sortable: false,
      suppressMenu: true,
      menuTabs: [],
    },
    onGridReady: ({ api }) => {
      this.gridApi = api
      this.preferences
        .getDensityLevel()
        .pipe(takeUntil(this.destroySubject$))
        .subscribe(level =>
          enableHeliosGridDensityLevel(api, DensityLevel.Compact === level),
        )
      enableHeliosCopyBehavior(api)
    },
    ...disableChangeDetectionOnScroll(this.changeDetector),
  }

  constructor(
    private readonly positionsService: PositionsService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly preferences: UserPreferencesService,
  ) {}

  ngOnDestroy(): void {
    this.destroySubject$.next()
    this.destroySubject$.complete()
    this.gridApi?.destroy()
  }

  /**
   * Method to get title into []
   *
   * @param positionComparisons
   * @returns
   */
  createPositionComparison(positionComparisons: PositionComparisons) {
    const posTypeCode = this._position?.posTypeCode
    if (!this.displayDynamicRows.value)
      return PositionComparisonTitles.map(
        ({ positionComparisonKey, displayTitle }) => ({
          ...positionComparisons[
            positionComparisonKey as keyof typeof positionComparisons
          ],
          title:
            positionComparisonKey === 'pendingUnits' &&
            (posTypeCode === 'Cash' || posTypeCode === 'Margin')
              ? 'Pending Cash'
              : displayTitle,
        }),
      )
    else return this.parseDynamicFields(positionComparisons, false, posTypeCode)
  }

  parseDynamicFields(
    data: PositionComparisonsExtToExt | PositionComparisons,
    isExtToExt: boolean,
    posTypeCode?: PositionType,
  ) {
    const result = isExtToExt
      ? PositionComparisonTitles.map(
          ({ positionComparisonKey, displayTitle }) => ({
            ...data[positionComparisonKey as keyof typeof data],
            title: displayTitle,
          }),
        )
      : PositionComparisonTitles.map(
          ({ positionComparisonKey, displayTitle }) => ({
            ...data[positionComparisonKey as keyof typeof data],
            title:
              positionComparisonKey === 'pendingUnits' &&
              (posTypeCode === 'Cash' || posTypeCode === 'Margin')
                ? 'Pending Cash'
                : displayTitle,
          }),
        )

    // --- DYNAMIC AGGINFO FIELD HANDLING ---
    const aggInfo = data.aggInfo
    const extValueSelectedAggInfo =
      isExtToExt && 'extValueSelected' in aggInfo
        ? JSON.parse('{ ' + aggInfo.extValueSelected + ' }')
        : 'extValue' in aggInfo
        ? JSON.parse('{ ' + aggInfo.extValue + ' }')
        : undefined

    const otherDataSourceAggInfo =
      isExtToExt && 'extValueOtherDataSource' in aggInfo
        ? JSON.parse('{ ' + aggInfo.extValueOtherDataSource + ' }')
        : 'cwValue' in aggInfo
        ? JSON.parse('{ ' + aggInfo.cwValue + ' }')
        : undefined

    let selectedFields: string[] = []
    let selectedFieldsFromOtherDataSource: string[] = []

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsedSelected: any = {}
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let parsedOther: any = {}

    if (extValueSelectedAggInfo) {
      parsedSelected = extValueSelectedAggInfo
      if (parsedSelected.heliosDisplay) {
        selectedFields = parsedSelected.heliosDisplay
          .split(',')
          .map((s: string) => s.trim())
      }
    }

    if (otherDataSourceAggInfo) {
      parsedOther = otherDataSourceAggInfo
      if (parsedOther.heliosDisplay) {
        selectedFieldsFromOtherDataSource = parsedOther.heliosDisplay
          .split(',')
          .map((s: string) => s.trim())
      }
    }

    // Union of all dynamic field names to display as rows
    const allDynamicFields = Array.from(
      new Set([...selectedFields, ...selectedFieldsFromOtherDataSource]),
    )

    allDynamicFields.forEach(field => {
      const extValueSelectedField = selectedFields.includes(field)
        ? parsedSelected[field]
        : undefined
      const extValueOtherDataSourceField =
        selectedFieldsFromOtherDataSource.includes(field)
          ? parsedOther[field]
          : undefined

      if (
        extValueSelectedField !== undefined ||
        extValueOtherDataSourceField !== undefined
      ) {
        if (!isExtToExt)
          result.push({
            extValue: extValueSelectedField,
            cwValue: extValueOtherDataSourceField,
            title: field.replace(/_/g, ' '),
          })
        else
          result.push({
            extValueSelected: extValueSelectedField,
            extValueOtherDataSource: extValueOtherDataSourceField,
            title: field.replace(/_/g, ' '),
          })
      }
    })

    return result
  }

  createPositionComparisonExtToExt(
    positionComparisonsExtToExt: PositionComparisonsExtToExt,
  ) {
    if (!this.displayDynamicRows.value)
      return PositionComparisonTitles.map(
        ({ positionComparisonKey, displayTitle }) => ({
          ...positionComparisonsExtToExt[
            positionComparisonKey as keyof typeof positionComparisonsExtToExt
          ],
          title: displayTitle,
        }),
      )
    else return this.parseDynamicFields(positionComparisonsExtToExt, true)
  }

  toggleDynamicRows() {
    this.reloadData()
    this.updatePropertiesTitle()
  }

  createColumnDefs(): ColDef[] {
    return [
      {
        field: 'title',
        headerName: '',
      },
      {
        field: this.isExternalToExternalComparison
          ? 'extValueSelected'
          : 'extValue',
        headerValueGetter: () => this._position?.extPosSourceType ?? 'External',
        valueGetter: ({ data }) => {
          const valueField = this.isExternalToExternalComparison
            ? data.extValueSelected
            : data.extValue
          if (data.title === 'Factor' || data.title === 'Identifier') {
            return valueField
          } else {
            return columnValueFormatter(valueField)
          }
        },
      },
      {
        field: this.isExternalToExternalComparison
          ? 'extValueOtherDataSource'
          : 'cwValue',
        headerValueGetter: () =>
          this.isExternalToExternalComparison
            ? this._position?.extPosSourceType?.toLowerCase() === 'trading'
              ? 'CUSTODY'
              : this._position?.extPosSourceType?.toLowerCase() === 'custody'
              ? 'TRADING'
              : 'External'
            : 'CWAN',
        valueGetter: ({ data }) => {
          const valueField = this.isExternalToExternalComparison
            ? data.extValueOtherDataSource
            : data.cwValue
          if (data.title === 'Factor' || data.title === 'Identifier') {
            return valueField
          } else {
            return columnValueFormatter(valueField)
          }
        },
      },
      {
        field: 'diff',
        headerName: 'Difference',
        valueGetter: ({ data }) => {
          if (data.title === 'Factor' || data.title === 'Identifier') {
            return data.diff
          } else {
            return columnValueFormatter(data.diff)
          }
        },
        cellClass: params => {
          return Math.abs(Math.trunc(params.value * 1000) / 1000) >= 0.005
            ? ' text-danger'
            : ''
        },
        tooltipValueGetter: ({ data }) => {
          if (data.title === 'Pending Units')
            return 'Pending Units Difference = Current Units Difference + CWAN Pending Units'
          else if (data.title === 'Pending Cash')
            return 'Pending Cash Difference = Current Units Difference - CWAN Pending Cash'
          else return data.diff
        },
      },
    ]
  }

  updatePropertiesTitle() {
    const title = []

    if (this.includeValuationData) title.push('Include valuation data.')
    if (this.clearedOnly) title.push('Comparisons based off cleared date.')
    if (this.displayDynamicRows.value) title.push('Showing Dynamic Rows.')

    this.propertiesTitle = title
  }

  reloadData() {
    if (this.gridApi) {
      this.gridApi?.setColumnDefs(this.createColumnDefs())
      this.gridApi?.refreshHeader()
    } else {
      this.gridOptions.columnDefs = this.createColumnDefs()
    }

    this.updatePropertiesTitle()

    // Don't load data on no position
    if (!this._position) {
      return
    }

    this.gridApi?.showLoadingOverlay()
    this.isExternalToExternalComparison
      ? this.positionsService
          .getPositionComparisonsExtToExt({
            positionIdSelected: this._position.positionId,
            positionIdOtherDataSource: this.getOtherSourcePositionId(
              this._position.positionId,
            ),
            clearedOnly: this.clearedOnly,
            // Currently ignored on the backend
            includeValuationData: this.includeValuationData,
          })
          .pipe(
            take(1),
            map(comparisons =>
              this.createPositionComparisonExtToExt(comparisons),
            ),
            tap(data => this.positionComparisonData.next(data)),
          )
          .subscribe()
      : this.positionsService
          .getPositionComparisons({
            positionId: this._position.positionId,
            clearedOnly: this.clearedOnly,
            // Currently ignored on the backend
            includeValuationData: this.includeValuationData,
          })
          .pipe(
            take(1),
            map(comparisons => this.createPositionComparison(comparisons)),
            tap(data => this.positionComparisonData.next(data)),
          )
          .subscribe()
  }

  getOtherSourcePositionId(positionId: string): string {
    return positionId.includes('-CUSTODY')
      ? positionId.replace('-CUSTODY', '-TRADING')
      : positionId.replace('-TRADING', '-CUSTODY')
  }
}
